'use server'

import { apiRequest, ApiResponse } from '../request'

export interface ResellerPlatformPercentageResponse {
    percentage: number
}

export async function getResellerPlatformPercentage(): Promise<
    ApiResponse<ResellerPlatformPercentageResponse>
> {
    return apiRequest<ResellerPlatformPercentageResponse>(
        'utils/settings/master/reseller-platform-percentage',
        {}
    )
}

export async function updateResellerPlatformPercentage(
    percentage: number
): Promise<ApiResponse<any>> {
    return apiRequest<any>(
        'utils/settings/master/reseller-platform-percentage',
        { data: { percentage } },
        'patch'
    )
}

export interface FinancialSettings {
    teleconsultationCostPerMinute: number
    teleconsultationSalePricePerMinute: number
    fiscalNoteCost: number
    fiscalNoteSalePrice: number
    signDocumentCost?: number
    signDocumentSalePrice?: number
    // Multiplicador do preço mínimo de revenda sobre os preços de venda (ex: 5).
    resellerResourceMultiplier?: number
    extraStorePercentage?: number
    // Valor de 1 ponto de fidelidade em centavos (mínimo 1). Uso futuro no resgate.
    pointValueCents?: number
    // Armazenamento adicional, por GB/mês enquanto a recorrência estiver ativa.
    storageGbCost?: number
    storageGbSalePrice?: number
    // Preço de venda de um crédito de IA (centavos). Zero: crédito fora de venda.
    aiCreditSalePrice?: number
    // Margem sobre o custo real da IA, em % do cobrado. Decide o débito.
    aiCreditMarginPercent?: number
    // Vídeo (MediarMeet): 1 crédito = 1 minuto por participante com vídeo. Zero desliga a cobrança.
    videoCreditSalePrice?: number
    videoViewerMinuteCredits?: number
    videoRecordingMinuteCredits?: number
}

export async function getFinancialSettings(): Promise<
    ApiResponse<FinancialSettings>
> {
    return apiRequest<FinancialSettings>(
        'utils/settings/master/financial-settings',
        {}
    )
}

export async function updateFinancialSettings(
    data: FinancialSettings
): Promise<ApiResponse<any>> {
    return apiRequest<any>(
        'utils/settings/master/financial-settings',
        { data },
        'patch'
    )
}

export interface ClientPointsBackfillResult {
    processed: number
    credited: number
    clients: number
}

/**
 * Atribui retroativamente os pontos de fidelidade a partir das cobranças já
 * pagas. Idempotente — cobranças já creditadas são ignoradas. Apenas MASTER.
 */
export async function backfillClientPoints(): Promise<
    ApiResponse<ClientPointsBackfillResult>
> {
    return apiRequest<ClientPointsBackfillResult>(
        'client-points/backfill',
        {},
        'post'
    )
}

export type OverdueMode = 'OFF' | 'UNTIL_DAYS' | 'FOREVER'

export interface DunningPolicy {
    preDueEnabled: boolean
    preDueDays: number[]
    overdueMode: OverdueMode
    overdueMaxDays: number
    overdueIntervalDays: number
    overdueStartDate: string
}

/** Política de armazenamento da plataforma: o que é bloqueado no upload. */
export interface StoragePolicy {
    blockedExtensions: string[]
}

export async function getStoragePolicy(): Promise<ApiResponse<StoragePolicy>> {
    return apiRequest<StoragePolicy>('utils/settings/master/storage-policy', {})
}

export async function updateStoragePolicy(
    data: StoragePolicy
): Promise<ApiResponse<StoragePolicy>> {
    return apiRequest<StoragePolicy>(
        'utils/settings/master/storage-policy',
        { data },
        'patch'
    )
}

export async function getDunningPolicy(): Promise<ApiResponse<DunningPolicy>> {
    return apiRequest<DunningPolicy>(
        'utils/settings/master/dunning-policy',
        {}
    )
}

export async function updateDunningPolicy(
    data: DunningPolicy
): Promise<ApiResponse<DunningPolicy>> {
    return apiRequest<DunningPolicy>(
        'utils/settings/master/dunning-policy',
        { data },
        'patch'
    )
}

